import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow, isPast, isWithinInterval, addDays } from 'date-fns';
import {
  Calendar,
  Clock,
  Plus,
  Search,
  AlertTriangle,
  CheckCircle,
  Wrench,
  Trash2,
  Play,
  Pause,
  MoreVertical,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  maintenanceScheduleApi,
  MaintenanceSchedule,
  MaintenanceScheduleDashboard,
  MAINTENANCE_FREQUENCIES,
  MAINTENANCE_PRIORITIES,
  SCHEDULE_STATUSES,
} from '@/api/services/maintenance-schedule.api';
import { equipmentApi, Equipment } from '@/api/services/equipment.api';

const priorityColors: Record<string, string> = {
  LOW: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  MEDIUM: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  HIGH: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  CRITICAL: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const statusColors: Record<string, string> = {
  ACTIVE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  PAUSED: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  COMPLETED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  CANCELLED: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

export default function MaintenanceSchedules() {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>([]);
  const [dashboard, setDashboard] = useState<MaintenanceScheduleDashboard | null>(null);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [frequencyFilter, setFrequencyFilter] = useState<string>('');
  const [showOverdue, setShowOverdue] = useState(false);
  const [showDueSoon, setShowDueSoon] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<MaintenanceSchedule | null>(null);
  const [isGenerateWOOpen, setIsGenerateWOOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    equipmentId: '',
    frequency: 'MONTHLY',
    intervalDays: '',
    intervalHours: '',
    nextDue: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    priority: 'MEDIUM',
    estimatedDuration: '',
    instructions: '',
    checklistItems: '',
  });

  useEffect(() => {
    loadData();
  }, [page, search, statusFilter, priorityFilter, frequencyFilter, showOverdue, showDueSoon]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (frequencyFilter) params.frequency = frequencyFilter;
      if (showOverdue) params.overdue = true;
      if (showDueSoon) params.dueSoon = true;

      const [schedulesRes, dashboardRes, equipmentRes] = await Promise.all([
        maintenanceScheduleApi.getAll(params),
        maintenanceScheduleApi.getDashboard(),
        equipmentApi.getAll({ limit: 100 }),
      ]);

      setSchedules(schedulesRes.data.data);
      setTotalPages(schedulesRes.data.pagination.totalPages);
      setDashboard(dashboardRes.data);
      setEquipment(equipmentRes.data.data);
    } catch (error) {
      toast.error('Failed to load maintenance schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const data: any = {
        name: formData.name,
        equipmentId: formData.equipmentId,
        frequency: formData.frequency,
        nextDue: formData.nextDue,
        priority: formData.priority,
      };

      if (formData.intervalDays) data.intervalDays = parseInt(formData.intervalDays);
      if (formData.intervalHours) data.intervalHours = parseFloat(formData.intervalHours);
      if (formData.estimatedDuration) data.estimatedDuration = parseFloat(formData.estimatedDuration);
      if (formData.instructions) data.instructions = formData.instructions;
      if (formData.checklistItems) {
        data.checklistItems = formData.checklistItems.split('\n').filter(Boolean);
      }

      await maintenanceScheduleApi.create(data);
      toast.success('Maintenance schedule created successfully');
      setIsCreateOpen(false);
      resetForm();
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create schedule');
    }
  };

  const handleStatusUpdate = async (id: string, status: string) => {
    try {
      await maintenanceScheduleApi.update(id, { status });
      toast.success('Schedule status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await maintenanceScheduleApi.delete(id);
      toast.success('Schedule deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const handleGenerateWorkOrder = async () => {
    if (!selectedSchedule) return;
    try {
      const result = await maintenanceScheduleApi.generateWorkOrder(selectedSchedule.id);
      toast.success(`Work order ${(result.data as any).workOrderNumber} created successfully`);
      setIsGenerateWOOpen(false);
      loadData();
      navigate(`/work-orders/${(result.data as any).id}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to generate work order');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      equipmentId: '',
      frequency: 'MONTHLY',
      intervalDays: '',
      intervalHours: '',
      nextDue: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
      priority: 'MEDIUM',
      estimatedDuration: '',
      instructions: '',
      checklistItems: '',
    });
  };

  const viewScheduleDetail = async (schedule: MaintenanceSchedule) => {
    try {
      const response = await maintenanceScheduleApi.getById(schedule.id);
      setSelectedSchedule(response.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load schedule details');
    }
  };

  const getDueStatus = (nextDue: string) => {
    const dueDate = new Date(nextDue);
    const now = new Date();
    const sevenDaysFromNow = addDays(now, 7);

    if (isPast(dueDate)) {
      return { label: 'Overdue', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
    }
    if (isWithinInterval(dueDate, { start: now, end: sevenDaysFromNow })) {
      return { label: 'Due Soon', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' };
    }
    return { label: 'On Track', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' };
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Maintenance Schedules</h1>
          <p className="text-sm text-muted-foreground">
            Manage preventive maintenance schedules for equipment
          </p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Schedule
        </Button>
      </div>

      {/* Dashboard Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Schedules</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{dashboard.active}</div>
              <p className="text-xs text-muted-foreground">of {dashboard.total} total</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Due This Week</CardTitle>
              <Clock className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">{dashboard.dueSoon}</div>
              <p className="text-xs text-muted-foreground">schedules due soon</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overdue</CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{dashboard.overdue}</div>
              <p className="text-xs text-muted-foreground">require attention</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Compliance Rate</CardTitle>
              <CheckCircle className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                {dashboard.total > 0
                  ? Math.round(((dashboard.active - dashboard.overdue) / dashboard.active) * 100)
                  : 100}
                %
              </div>
              <p className="text-xs text-muted-foreground">on-time compliance</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search schedules..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {SCHEDULE_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={(v) => setPriorityFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {MAINTENANCE_PRIORITIES.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={frequencyFilter} onValueChange={(v) => setFrequencyFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Frequency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Frequencies</SelectItem>
            {MAINTENANCE_FREQUENCIES.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={showOverdue ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            setShowOverdue(!showOverdue);
            setShowDueSoon(false);
          }}
        >
          <AlertTriangle className="mr-1 h-4 w-4" />
          Overdue
        </Button>
        <Button
          variant={showDueSoon ? 'default' : 'outline'}
          size="sm"
          onClick={() => {
            setShowDueSoon(!showDueSoon);
            setShowOverdue(false);
          }}
        >
          <Clock className="mr-1 h-4 w-4" />
          Due Soon
        </Button>
      </div>

      {/* Schedules Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Schedule Name</TableHead>
              <TableHead>Equipment</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead>Next Due</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <div className="flex items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                </TableCell>
              </TableRow>
            ) : schedules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No maintenance schedules found
                </TableCell>
              </TableRow>
            ) : (
              schedules.map((schedule) => {
                const dueStatus = getDueStatus(schedule.nextDue);
                return (
                  <TableRow
                    key={schedule.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => viewScheduleDetail(schedule)}
                  >
                    <TableCell className="font-medium">{schedule.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Wrench className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="text-sm">{schedule.equipment?.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {schedule.equipment?.assetNumber}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {MAINTENANCE_FREQUENCIES.find((f) => f.value === schedule.frequency)?.label}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-sm">
                          {format(new Date(schedule.nextDue), 'MMM d, yyyy')}
                        </div>
                        <Badge variant="outline" className={dueStatus.color}>
                          {dueStatus.label}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={priorityColors[schedule.priority]}>
                        {schedule.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[schedule.status]}>{schedule.status}</Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedSchedule(schedule);
                              setIsGenerateWOOpen(true);
                            }}
                          >
                            <Wrench className="mr-2 h-4 w-4" />
                            Generate Work Order
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {schedule.status === 'ACTIVE' ? (
                            <DropdownMenuItem onClick={() => handleStatusUpdate(schedule.id, 'PAUSED')}>
                              <Pause className="mr-2 h-4 w-4" />
                              Pause Schedule
                            </DropdownMenuItem>
                          ) : schedule.status === 'PAUSED' ? (
                            <DropdownMenuItem onClick={() => handleStatusUpdate(schedule.id, 'ACTIVE')}>
                              <Play className="mr-2 h-4 w-4" />
                              Resume Schedule
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem
                            onClick={() => handleDelete(schedule.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create Schedule Sheet */}
      <Sheet open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create Maintenance Schedule</SheetTitle>
            <SheetDescription>
              Set up a new preventive maintenance schedule for equipment
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Schedule Name</Label>
              <Input
                id="name"
                placeholder="e.g., Monthly Filter Replacement"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="equipment">Equipment</Label>
              <Select
                value={formData.equipmentId}
                onValueChange={(v) => setFormData({ ...formData, equipmentId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select equipment" />
                </SelectTrigger>
                <SelectContent>
                  {equipment.map((eq) => (
                    <SelectItem key={eq.id} value={eq.id}>
                      {eq.name} ({eq.assetNumber})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="frequency">Frequency</Label>
                <Select
                  value={formData.frequency}
                  onValueChange={(v) => setFormData({ ...formData, frequency: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MAINTENANCE_FREQUENCIES.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(v) => setFormData({ ...formData, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MAINTENANCE_PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="nextDue">Next Due Date</Label>
              <Input
                id="nextDue"
                type="date"
                value={formData.nextDue}
                onChange={(e) => setFormData({ ...formData, nextDue: e.target.value })}
              />
            </div>
            {(formData.frequency === 'HOURS_BASED' || formData.frequency === 'CONDITION_BASED') && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="intervalDays">Interval (Days)</Label>
                  <Input
                    id="intervalDays"
                    type="number"
                    placeholder="30"
                    value={formData.intervalDays}
                    onChange={(e) => setFormData({ ...formData, intervalDays: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="intervalHours">Interval (Hours)</Label>
                  <Input
                    id="intervalHours"
                    type="number"
                    placeholder="500"
                    value={formData.intervalHours}
                    onChange={(e) => setFormData({ ...formData, intervalHours: e.target.value })}
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="estimatedDuration">Estimated Duration (Hours)</Label>
              <Input
                id="estimatedDuration"
                type="number"
                step="0.5"
                placeholder="2.0"
                value={formData.estimatedDuration}
                onChange={(e) => setFormData({ ...formData, estimatedDuration: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="instructions">Instructions</Label>
              <Textarea
                id="instructions"
                placeholder="Maintenance instructions and procedures..."
                rows={3}
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="checklist">Checklist Items (one per line)</Label>
              <Textarea
                id="checklist"
                placeholder="Check oil level&#10;Replace filter&#10;Inspect belts"
                rows={4}
                value={formData.checklistItems}
                onChange={(e) => setFormData({ ...formData, checklistItems: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!formData.name || !formData.equipmentId}
              >
                Create Schedule
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Schedule Detail Sheet */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{selectedSchedule?.name}</SheetTitle>
            <SheetDescription>
              {selectedSchedule?.equipment?.name} ({selectedSchedule?.equipment?.assetNumber})
            </SheetDescription>
          </SheetHeader>
          {selectedSchedule && (
            <div className="mt-6 space-y-6">
              <div className="flex gap-2">
                <Badge className={statusColors[selectedSchedule.status]}>
                  {selectedSchedule.status}
                </Badge>
                <Badge className={priorityColors[selectedSchedule.priority]}>
                  {selectedSchedule.priority}
                </Badge>
                <Badge variant="outline">
                  {MAINTENANCE_FREQUENCIES.find((f) => f.value === selectedSchedule.frequency)?.label}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Next Due</Label>
                  <div className="font-medium">
                    {format(new Date(selectedSchedule.nextDue), 'MMM d, yyyy')}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(selectedSchedule.nextDue), { addSuffix: true })}
                  </div>
                </div>
                {selectedSchedule.lastPerformed && (
                  <div>
                    <Label className="text-muted-foreground">Last Performed</Label>
                    <div className="font-medium">
                      {format(new Date(selectedSchedule.lastPerformed), 'MMM d, yyyy')}
                    </div>
                  </div>
                )}
                {selectedSchedule.estimatedDuration && (
                  <div>
                    <Label className="text-muted-foreground">Est. Duration</Label>
                    <div className="font-medium">{selectedSchedule.estimatedDuration} hours</div>
                  </div>
                )}
              </div>

              {selectedSchedule.instructions && (
                <div>
                  <Label className="text-muted-foreground">Instructions</Label>
                  <p className="mt-1 text-sm whitespace-pre-wrap">{selectedSchedule.instructions}</p>
                </div>
              )}

              {selectedSchedule.checklistItems && selectedSchedule.checklistItems.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Checklist</Label>
                  <ul className="mt-1 space-y-1">
                    {selectedSchedule.checklistItems.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <div className="h-4 w-4 rounded border border-muted-foreground/30" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedSchedule.requiredParts && selectedSchedule.requiredParts.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">Required Parts</Label>
                  <div className="mt-2 space-y-2">
                    {selectedSchedule.requiredParts.map((part) => (
                      <div
                        key={part.id}
                        className="flex items-center justify-between rounded-lg border p-3"
                      >
                        <div>
                          <div className="font-medium">{part.item?.name}</div>
                          <div className="text-sm text-muted-foreground">{part.item?.sku}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">
                            {part.quantity} {part.unit}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  className="flex-1"
                  onClick={() => {
                    setIsDetailOpen(false);
                    setIsGenerateWOOpen(true);
                  }}
                >
                  <Wrench className="mr-2 h-4 w-4" />
                  Generate Work Order
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Generate Work Order Confirmation */}
      <Dialog open={isGenerateWOOpen} onOpenChange={setIsGenerateWOOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Work Order</DialogTitle>
            <DialogDescription>
              This will create a new work order based on the maintenance schedule &quot;
              {selectedSchedule?.name}&quot; and update the next due date.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Equipment:</span>
                <span className="font-medium">{selectedSchedule?.equipment?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Priority:</span>
                <Badge className={priorityColors[selectedSchedule?.priority || 'MEDIUM']}>
                  {selectedSchedule?.priority}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Required Parts:</span>
                <span className="font-medium">
                  {selectedSchedule?.requiredParts?.length || 0} items
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGenerateWOOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGenerateWorkOrder}>Generate Work Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
